import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Badge } from './ui/badge.jsx';
import { Users, AlertTriangle, CheckCircle, Clock, FileText, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HRDashboard = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState({ employees: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, expired, expiring, no_certs

  useEffect(() => {
    fetchCertificatesOverview();
  }, []);

  const fetchCertificatesOverview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr/certificates-overview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setOverview(response.data);
    } catch (error) {
      console.error('Error fetching certificates overview:', error);
      toast.error('Failed to load certificates overview');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredEmployees = () => {
    if (filter === 'all') return overview.employees;
    if (filter === 'expired') return overview.employees.filter(e => e.expired > 0);
    if (filter === 'expiring') return overview.employees.filter(e => (e.expiring_30 + e.expiring_60 + e.expiring_90) > 0);
    if (filter === 'no_certs') return overview.employees.filter(e => e.total_certificates === 0);
    return overview.employees;
  };

  const getExpiryBadge = (status) => {
    const badges = {
      'expired': <Badge className="bg-red-500 text-white">Expired</Badge>,
      'expiring_30': <Badge className="bg-red-400 text-white animate-pulse">30 Days</Badge>,
      'expiring_60': <Badge className="bg-orange-500 text-white">60 Days</Badge>,
      'expiring_90': <Badge className="bg-yellow-500 text-white">90 Days</Badge>,
      'active': <Badge className="bg-green-500 text-white">Active</Badge>,
      'no_expiry': <Badge className="bg-blue-500 text-white">No Expiry</Badge>,
      'superseded': <Badge className="bg-slate-300 text-slate-700">Replaced by newer card</Badge>,
      'duplicate': <Badge className="bg-slate-200 text-slate-600">Duplicate scan</Badge>
    };
    return badges[status] || <Badge>Unknown</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="animate-fade-in p-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-8 drop-shadow-lg">HR Dashboard - Employee Certificates</h1>
        <div className="text-slate-900">Loading...</div>
      </div>
    );
  }

  const filteredEmployees = getFilteredEmployees();

  return (
    <div className="animate-fade-in p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2 drop-shadow-lg">HR Dashboard</h1>
        <p className="text-slate-600 font-semibold drop-shadow">Employee Safety Certificates & Compliance Tracking</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover:shadow-2xl transition-all">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-semibold">Total Employees</p>
                <p className="text-3xl font-bold text-slate-900">{overview.summary.total_employees || 0}</p>
              </div>
              <div className="h-14 w-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <Users className="h-7 w-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover:shadow-2xl transition-all">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-semibold">Total Certificates</p>
                <p className="text-3xl font-bold text-slate-900">{overview.summary.total_certificates || 0}</p>
              </div>
              <div className="h-14 w-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                <FileText className="h-7 w-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover:shadow-2xl transition-all cursor-pointer" onClick={() => setFilter('expired')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-semibold">Expired</p>
                <p className="text-3xl font-bold text-red-600">{overview.summary.total_expired || 0}</p>
              </div>
              <div className="h-14 w-14 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                <AlertTriangle className="h-7 w-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover:shadow-2xl transition-all cursor-pointer" onClick={() => setFilter('expiring')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-semibold">Expiring (90 Days)</p>
                <p className="text-3xl font-bold text-orange-600">
                  {(overview.summary.total_expiring_30 || 0) + (overview.summary.total_expiring_60 || 0) + (overview.summary.total_expiring_90 || 0)}
                </p>
              </div>
              <div className="h-14 w-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                <Clock className="h-7 w-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            filter === 'all' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white/60 text-slate-700 hover:bg-white/80'
          }`}
        >
          All Employees ({overview.employees.length})
        </button>
        <button
          onClick={() => setFilter('expired')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            filter === 'expired' ? 'bg-red-600 text-white shadow-lg' : 'bg-white/60 text-slate-700 hover:bg-white/80'
          }`}
        >
          Expired ({overview.employees.filter(e => e.expired > 0).length})
        </button>
        <button
          onClick={() => setFilter('expiring')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            filter === 'expiring' ? 'bg-orange-600 text-white shadow-lg' : 'bg-white/60 text-slate-700 hover:bg-white/80'
          }`}
        >
          Expiring Soon ({overview.employees.filter(e => (e.expiring_30 + e.expiring_60 + e.expiring_90) > 0).length})
        </button>
        <button
          onClick={() => setFilter('no_certs')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            filter === 'no_certs' ? 'bg-slate-200 text-slate-900 shadow-lg' : 'bg-white/60 text-slate-700 hover:bg-white/80'
          }`}
        >
          No Certificates ({overview.employees.filter(e => e.total_certificates === 0).length})
        </button>
      </div>

      {/* Employee List */}
      <Card className="bg-white/60 backdrop-blur-xl border-slate-200">
        <CardHeader className="border-b border-slate-200">
          <CardTitle className="text-slate-900 font-bold">Employee Certificates</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {filteredEmployees.map((employee) => (
              <div key={employee.employee_id} className="bg-white/80 rounded-lg p-4 border border-slate-200 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{employee.employee_name}</h3>
                    <p className="text-sm text-slate-600">{employee.employee_email}</p>
                  </div>
                  <div className="flex gap-2">
                    {employee.expired > 0 && (
                      <Badge className="bg-red-500 text-white">{employee.expired} Expired</Badge>
                    )}
                    {employee.expiring_30 > 0 && (
                      <Badge className="bg-red-400 text-white animate-pulse">{employee.expiring_30} Expiring (30d)</Badge>
                    )}
                    {employee.expiring_60 > 0 && (
                      <Badge className="bg-orange-500 text-white">{employee.expiring_60} Expiring (60d)</Badge>
                    )}
                    {employee.expiring_90 > 0 && (
                      <Badge className="bg-yellow-500 text-white">{employee.expiring_90} Expiring (90d)</Badge>
                    )}
                    {employee.total_certificates === 0 && (
                      <Badge className="bg-gray-500 text-slate-900">No Certificates</Badge>
                    )}
                  </div>
                </div>

                {/* Certificates List */}
                {employee.certificates.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {employee.certificates.map((cert) => (
                      <div key={cert.id} className="flex items-center justify-between p-3 bg-slate-50 rounded border border-slate-200">
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">{cert.display_name || cert.certificate_type || cert.course_name}</p>
                          {cert.superseded_by && <p className="text-xs text-slate-500">Replaced by “{cert.superseded_by.name}”</p>}
                          <p className="text-xs text-slate-500">
                            Issued: {formatDate(cert.issue_date)} | Expires: {formatDate(cert.effective_expiry || cert.expiry_date)}{cert.expiry_source === 'name' ? ' (from file name)' : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getExpiryBadge(cert.expiry_status)}
                          {(cert.google_drive_file_id || cert.certificate_file_url) && (
                            <a
                              href={cert.google_drive_file_id 
                                ? `${process.env.REACT_APP_BACKEND_URL}/api/certificate-file/${cert.google_drive_file_id}`
                                : cert.certificate_file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                              title="View Certificate File"
                            >
                              <ExternalLink className="h-5 w-5" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {filteredEmployees.length === 0 && (
              <div className="text-center py-12">
                <Users className="h-16 w-16 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-600 text-lg font-semibold">No employees match the selected filter</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HRDashboard;
